import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Button,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
  useToast,
  useDisclosure,
  Card,
  CardBody,
  HStack,
  VStack,
  SimpleGrid,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Portal,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEllipsisV,
  faEdit,
  faTrash,
  faBuilding,
  faBullhorn,
  faEnvelope,
  faImage,
} from '@fortawesome/free-solid-svg-icons';
import { useAdvertisers, useAdvertiserMutations } from '../hooks/useAdvertisers';
import { formatNumber, formatPercent } from '../utils/format';
import AdvertiserModal from '../components/advertisers/AdvertiserModal';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import { useLicenseProtection } from '../hooks/useLicenseProtection';
import DateRangePicker from '../components/common/DateRangePicker';

function AdvertisersPage() {
  const navigate = useNavigate();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const toast = useToast();
  const { isReadOnly, protectAction } = useLicenseProtection();

  // Modal states
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const [selectedAdvertiser, setSelectedAdvertiser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Date range state
  const [dateRange, setDateRange] = useState({ preset: '30d' });
  const handleDateRangeChange = useCallback((range) => setDateRange(range), []);

  // Build API filters from date range
  const apiFilters = useMemo(() => {
    const params = {};
    if (dateRange.start) params.start = dateRange.start.toISOString();
    if (dateRange.end) params.end = dateRange.end.toISOString();
    return Object.keys(params).length ? params : undefined;
  }, [dateRange]);

  // Fetch data
  const { data: advertisers, loading, error, refetch } = useAdvertisers(apiFilters);
  const { createAdvertiser, updateAdvertiser, deleteAdvertiser } = useAdvertiserMutations();

  const handleEdit = (advertiser) => {
    setSelectedAdvertiser(advertiser);
    onEditOpen();
  };

  const handleDeleteClick = (advertiser) => {
    setSelectedAdvertiser(advertiser);
    onDeleteOpen();
  };

  const handleCreateSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await createAdvertiser(data);
      toast({
        title: 'Advertiser created',
        status: 'success',
        duration: 3000,
      });
      onCreateClose();
      refetch();
    } catch (err) {
      toast({
        title: 'Error creating advertiser',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (data) => {
    if (!selectedAdvertiser) return;
    setIsSubmitting(true);
    try {
      await updateAdvertiser(selectedAdvertiser.advertiser_id, data);
      toast({
        title: 'Advertiser updated',
        status: 'success',
        duration: 3000,
      });
      onEditClose();
      refetch();
    } catch (err) {
      toast({
        title: 'Error updating advertiser',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAdvertiser) return;
    setIsSubmitting(true);
    try {
      await deleteAdvertiser(selectedAdvertiser.advertiser_id);
      toast({
        title: 'Advertiser deleted',
        status: 'success',
        duration: 3000,
      });
      onDeleteClose();
      refetch();
    } catch (err) {
      toast({
        title: 'Error deleting advertiser',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'paused':
        return 'yellow';
      case 'inactive':
        return 'gray';
      default:
        return 'gray';
    }
  };

  // Calculate totals
  const totals = advertisers?.reduce(
    (acc, adv) => ({
      campaigns: acc.campaigns + (parseInt(adv.campaign_count) || 0),
      impressions: acc.impressions + (parseInt(adv.total_impressions) || 0),
      clicks: acc.clicks + (parseInt(adv.total_clicks) || 0),
    }),
    { campaigns: 0, impressions: 0, clicks: 0 }
  ) || { campaigns: 0, impressions: 0, clicks: 0 };

  return (
    <Box>
      {/* Page Header */}
      <Flex
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        mb={{ base: 6, md: 8 }}
        gap={4}
      >
        <Box>
          <Heading
            size={{ base: 'lg', md: 'xl' }}
            bgGradient="linear(to-r, brand.500, accent.500)"
            bgClip="text"
            fontWeight="700"
          >
            Advertisers
          </Heading>
          <Text color={textSecondary} mt={1}>
            Manage your advertising clients and track their performance
          </Text>
        </Box>
        <HStack spacing={3}>
          <DateRangePicker
            value={dateRange.preset}
            onChange={handleDateRangeChange}
            showCompare={false}
            size="sm"
          />
          <Button
            leftIcon={<FontAwesomeIcon icon={faPlus} />}
            colorScheme="brand"
            size={{ base: 'sm', md: 'md' }}
            borderRadius="xl"
            onClick={() => protectAction(onCreateOpen)}
            isDisabled={isReadOnly}
            title={isReadOnly ? 'License required to create advertisers' : undefined}
          >
            New Advertiser
          </Button>
        </HStack>
      </Flex>

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
        <Card bg={cardBg} border="1px solid" borderColor={borderColor} position="relative" overflow="hidden">
          <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, brand.400, brand.600)" />
          <CardBody py={4}>
            <VStack align="start" spacing={1}>
              <Text color={textSecondary} fontSize="sm">Total Advertisers</Text>
              <Text fontSize="2xl" fontWeight="bold">{advertisers?.length || 0}</Text>
            </VStack>
          </CardBody>
        </Card>
        <Card bg={cardBg} border="1px solid" borderColor={borderColor} position="relative" overflow="hidden">
          <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, blue.400, blue.600)" />
          <CardBody py={4}>
            <VStack align="start" spacing={1}>
              <Text color={textSecondary} fontSize="sm">Total Campaigns</Text>
              <Text fontSize="2xl" fontWeight="bold">{formatNumber(totals.campaigns)}</Text>
            </VStack>
          </CardBody>
        </Card>
        <Card bg={cardBg} border="1px solid" borderColor={borderColor} position="relative" overflow="hidden">
          <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, purple.400, purple.600)" />
          <CardBody py={4}>
            <VStack align="start" spacing={1}>
              <Text color={textSecondary} fontSize="sm">Total Impressions</Text>
              <Text fontSize="2xl" fontWeight="bold">{formatNumber(totals.impressions)}</Text>
            </VStack>
          </CardBody>
        </Card>
        <Card bg={cardBg} border="1px solid" borderColor={borderColor} position="relative" overflow="hidden">
          <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, green.400, green.600)" />
          <CardBody py={4}>
            <VStack align="start" spacing={1}>
              <Text color={textSecondary} fontSize="sm">Total Clicks</Text>
              <Text fontSize="2xl" fontWeight="bold">{formatNumber(totals.clicks)}</Text>
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Error State */}
      {error && (
        <Alert status="error" borderRadius="xl" mb={6}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading || advertisers === null ? (
        <Center py={20}>
          <Spinner size="xl" color="purple.500" thickness="3px" />
        </Center>
      ) : advertisers.length > 0 ? (
        <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" overflow="hidden">
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Advertiser</Th>
                  <Th>Contact</Th>
                  <Th isNumeric>Campaigns</Th>
                  <Th isNumeric>Ads</Th>
                  <Th isNumeric>Impressions</Th>
                  <Th isNumeric>Clicks</Th>
                  <Th isNumeric>CTR</Th>
                  <Th>Status</Th>
                  <Th width="50px"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {advertisers.map((advertiser) => (
                  <Tr key={advertiser.advertiser_id}>
                    <Td>
                      <HStack spacing={3}>
                        <Box
                          p={2}
                          bg="purple.100"
                          borderRadius="lg"
                          color="purple.500"
                          _dark={{ bg: 'purple.900', color: 'purple.200' }}
                        >
                          <FontAwesomeIcon icon={faBuilding} />
                        </Box>
                        <Text
                          fontWeight="500"
                          color="brand.500"
                          cursor="pointer"
                          _hover={{ textDecoration: 'underline' }}
                          onClick={() => handleEdit(advertiser)}
                        >
                          {advertiser.advertiser_name}
                        </Text>
                      </HStack>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        {advertiser.contact_name && (
                          <Text fontSize="sm">{advertiser.contact_name}</Text>
                        )}
                        {advertiser.contact_email && (
                          <HStack spacing={1} color={textSecondary} fontSize="xs">
                            <FontAwesomeIcon icon={faEnvelope} size="xs" />
                            <Text>{advertiser.contact_email}</Text>
                          </HStack>
                        )}
                      </VStack>
                    </Td>
                    <Td isNumeric>
                      <Text
                        fontWeight="500"
                        color={advertiser.campaign_count > 0 ? 'purple.500' : undefined}
                        cursor={advertiser.campaign_count > 0 ? 'pointer' : 'default'}
                        _hover={advertiser.campaign_count > 0 ? { textDecoration: 'underline' } : {}}
                        onClick={() => advertiser.campaign_count > 0 && navigate(`/campaigns?advertiser=${advertiser.advertiser_id}`)}
                      >
                        {advertiser.campaign_count || 0}
                      </Text>
                    </Td>
                    <Td isNumeric>
                      <Text
                        fontWeight="500"
                        color={advertiser.ad_count > 0 ? 'purple.500' : undefined}
                        cursor={advertiser.ad_count > 0 ? 'pointer' : 'default'}
                        _hover={advertiser.ad_count > 0 ? { textDecoration: 'underline' } : {}}
                        onClick={() => advertiser.ad_count > 0 && navigate(`/ads?advertiser=${advertiser.advertiser_id}`)}
                      >
                        {advertiser.ad_count || 0}
                      </Text>
                    </Td>
                    <Td isNumeric>
                      <Text>{formatNumber(advertiser.total_impressions || 0)}</Text>
                    </Td>
                    <Td isNumeric>
                      <Text>{formatNumber(advertiser.total_clicks || 0)}</Text>
                    </Td>
                    <Td isNumeric>
                      <Text color={parseFloat(advertiser.ctr) > 0 ? 'green.500' : textSecondary}>
                        {formatPercent(advertiser.ctr || 0, 2)}
                      </Text>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={getStatusColor(advertiser.status)}
                        borderRadius="full"
                        px={2}
                      >
                        {advertiser.status || 'active'}
                      </Badge>
                    </Td>
                    <Td>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<FontAwesomeIcon icon={faEllipsisV} />}
                          variant="ghost"
                          size="sm"
                          borderRadius="lg"
                        />
                        <Portal>
                          <MenuList zIndex={9999} py={2} borderRadius="xl">
                            <MenuItem
                              icon={<FontAwesomeIcon icon={faBullhorn} />}
                              onClick={() => navigate(`/campaigns?advertiser=${advertiser.advertiser_id}`)}
                              mx={2}
                              borderRadius="lg"
                              w="calc(100% - 16px)"
                            >
                              View Campaigns
                            </MenuItem>
                            <MenuItem
                              icon={<FontAwesomeIcon icon={faImage} />}
                              onClick={() => navigate(`/ads?advertiser=${advertiser.advertiser_id}`)}
                              mx={2}
                              borderRadius="lg"
                              w="calc(100% - 16px)"
                            >
                              View Ads
                            </MenuItem>
                            <MenuItem
                              icon={<FontAwesomeIcon icon={faEdit} />}
                              onClick={() => protectAction(() => handleEdit(advertiser))}
                              isDisabled={isReadOnly}
                              mx={2}
                              borderRadius="lg"
                              w="calc(100% - 16px)"
                            >
                              Edit
                            </MenuItem>
                            <MenuItem
                              icon={<FontAwesomeIcon icon={faTrash} />}
                              color={isReadOnly ? 'gray.400' : 'red.500'}
                              onClick={() => protectAction(() => handleDeleteClick(advertiser))}
                              isDisabled={isReadOnly}
                              mx={2}
                              borderRadius="lg"
                              w="calc(100% - 16px)"
                            >
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Portal>
                      </Menu>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Card>
      ) : (
        <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardBody>
            <VStack py={8} spacing={4}>
              <Box
                p={4}
                bg="purple.100"
                borderRadius="full"
                color="purple.500"
                _dark={{ bg: 'purple.900', color: 'purple.200' }}
              >
                <FontAwesomeIcon icon={faBuilding} size="2x" />
              </Box>
              <Text color={textSecondary} textAlign="center">
                No advertisers yet. Create your first advertiser to get started.
              </Text>
              <Button
                colorScheme="brand"
                borderRadius="xl"
                onClick={() => protectAction(onCreateOpen)}
                isDisabled={isReadOnly}
              >
                Create Advertiser
              </Button>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Modals */}
      <AdvertiserModal
        isOpen={isCreateOpen}
        onClose={onCreateClose}
        onSubmit={handleCreateSubmit}
        isLoading={isSubmitting}
      />

      <AdvertiserModal
        isOpen={isEditOpen}
        onClose={onEditClose}
        onSubmit={handleEditSubmit}
        advertiser={selectedAdvertiser}
        isLoading={isSubmitting}
      />

      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleDeleteConfirm}
        title="Advertiser"
        itemName={selectedAdvertiser?.advertiser_name}
        isLoading={isSubmitting}
      />
    </Box>
  );
}

export default AdvertisersPage;
